import { useEffect, useState, useCallback } from "react";
import { useOperator } from "@/context/OperatorContext";
import api, { formatApiError } from "@/lib/api";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, Loader2, FileText, Bike, CheckCircle2, Power, Trash2, Camera, QrCode } from "lucide-react";
import { toast } from "sonner";

const STATUS = {
  draft: "bg-muted text-muted-foreground", active: "bg-status-available/15 text-status-available",
  ended: "bg-status-overdue/15 text-status-overdue",
};
const INVOICE_STATUS = {
  pending: "bg-status-overdue/15 text-status-overdue", paid: "bg-status-available/15 text-status-available",
  overdue: "bg-status-recovery/15 text-status-recovery",
};

function field(set, k) { return (e) => set((f) => ({ ...f, [k]: e.target.value })); }

function NewAccountDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ company_name: "", gst_number: "", contact_person: "", contact_phone: "", address: "" });
  const submit = async () => {
    setBusy(true);
    try { await api.post("/operator/business/accounts", f); toast.success("Business account created"); setOpen(false); setF({ company_name: "", gst_number: "", contact_person: "", contact_phone: "", address: "" }); onCreated?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="biz-new-account-btn" size="sm" variant="outline" className="gap-1.5"><Plus className="h-4 w-4" /> New account</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>New business account</DialogTitle><DialogDescription>Light corporate KYC — the business is the accountable entity.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Company name</Label><Input data-testid="biz-company-name" value={f.company_name} onChange={field(setF, "company_name")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>GST / reg no.</Label><Input data-testid="biz-gst" value={f.gst_number} onChange={field(setF, "gst_number")} /></div>
            <div className="space-y-1.5"><Label>Contact phone</Label><Input data-testid="biz-phone" value={f.contact_phone} onChange={field(setF, "contact_phone")} /></div>
          </div>
          <div className="space-y-1.5"><Label>Contact person</Label><Input data-testid="biz-contact" value={f.contact_person} onChange={field(setF, "contact_person")} /></div>
          <div className="space-y-1.5"><Label>Address</Label><Input data-testid="biz-address" value={f.address} onChange={field(setF, "address")} /></div>
        </div>
        <DialogFooter><Button data-testid="biz-create-account" onClick={submit} disabled={busy} className="gap-2">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NewContractDialog({ accounts, onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ business_account_id: "", billing_cycle: "monthly", per_vehicle_rate: "", deposit_per_vehicle: "", vehicle_policy: "flexible", payment_method: "qr", credit_net_days: 15 });
  const submit = async () => {
    if (!f.business_account_id || !f.per_vehicle_rate) { toast.error("Pick an account and set a rate"); return; }
    setBusy(true);
    try {
      await api.post("/operator/business/contracts", { ...f, per_vehicle_rate: Number(f.per_vehicle_rate), deposit_per_vehicle: Number(f.deposit_per_vehicle) || 0, credit_net_days: Number(f.credit_net_days) || 15 });
      toast.success("Contract created (draft)"); setOpen(false); onCreated?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const sel = "w-full rounded-md border border-input bg-background px-3 py-2 text-sm";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="biz-new-contract-btn" size="sm" className="gap-1.5 bg-tenant-primary text-tenant-primary-foreground hover:opacity-90"><Plus className="h-4 w-4" /> New contract</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>New fleet contract</DialogTitle><DialogDescription>Consolidated billing = per-vehicle rate × active vehicles.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Business account</Label>
            <select data-testid="biz-contract-account" value={f.business_account_id} onChange={field(setF, "business_account_id")} className={sel}>
              <option value="">Select account…</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.company_name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Billing cycle</Label>
              <select data-testid="biz-cycle" value={f.billing_cycle} onChange={field(setF, "billing_cycle")} className={sel}><option value="monthly">Monthly</option><option value="weekly">Weekly</option></select></div>
            <div className="space-y-1.5"><Label>Vehicle set</Label>
              <select data-testid="biz-policy" value={f.vehicle_policy} onChange={field(setF, "vehicle_policy")} className={sel}><option value="flexible">Flexible (add/swap)</option><option value="fixed">Fixed for term</option></select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Rate / vehicle (₹)</Label><Input data-testid="biz-rate" type="number" value={f.per_vehicle_rate} onChange={field(setF, "per_vehicle_rate")} /></div>
            <div className="space-y-1.5"><Label>Deposit / vehicle (₹)</Label><Input data-testid="biz-deposit" type="number" value={f.deposit_per_vehicle} onChange={field(setF, "deposit_per_vehicle")} /></div>
          </div>
          <div className="space-y-1.5"><Label>Payment method</Label>
            <select data-testid="biz-payment-method" value={f.payment_method} onChange={field(setF, "payment_method")} className={sel}><option value="qr">UPI QR (manual confirm)</option><option value="credit">Credit / NET terms</option></select></div>
        </div>
        <DialogFooter><Button data-testid="biz-create-contract" onClick={submit} disabled={busy} className="gap-2">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignVehicleDialog({ contractId, vehicles, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ vehicle_id: "", odometer_out: "", photos: 0 });
  const available = vehicles.filter((v) => v.status === "available");
  const submit = async () => {
    if (!f.vehicle_id) { toast.error("Pick a vehicle"); return; }
    setBusy(true);
    try { await api.post(`/operator/business/contracts/${contractId}/vehicles`, { vehicle_id: f.vehicle_id, odometer_out: f.odometer_out === "" ? null : Number(f.odometer_out), photos: f.photos }); toast.success("Vehicle added"); setOpen(false); setF({ vehicle_id: "", odometer_out: "", photos: 0 }); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="biz-assign-vehicle-btn" size="sm" variant="outline" className="gap-1.5"><Bike className="h-3.5 w-3.5" /> Add vehicle</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Add vehicle to contract</DialogTitle><DialogDescription>Quick handover — odometer + photos when the bike goes out.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Vehicle</Label>
            <select data-testid="biz-assign-vehicle" value={f.vehicle_id} onChange={field(setF, "vehicle_id")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">Select available vehicle…</option>
              {available.map((v) => <option key={v.id} value={v.id}>{v.reg_number} · {v.model}</option>)}
            </select>
          </div>
          <div className="space-y-1.5"><Label>Odometer out (km)</Label><Input data-testid="biz-odo-out" type="number" value={f.odometer_out} onChange={field(setF, "odometer_out")} /></div>
          <Button data-testid="biz-photos" type="button" variant={f.photos ? "default" : "outline"} className="w-full gap-2" onClick={() => setF((x) => ({ ...x, photos: 4 }))}><Camera className="h-4 w-4" /> {f.photos ? "4 photos captured ✓" : "Capture photos"}</Button>
        </div>
        <DialogFooter><Button data-testid="biz-add-vehicle-submit" onClick={submit} disabled={busy} className="gap-2">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Add</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GenerateInvoiceDialog({ contractId, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dep, setDep] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/operator/business/contracts/${contractId}/invoices`, { include_deposit: dep }); toast.success("Invoice generated"); setOpen(false); setDep(false); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="biz-generate-invoice-btn" size="sm" variant="outline" className="gap-1.5"><FileText className="h-3.5 w-3.5" /> Generate invoice</Button></DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Generate invoice</DialogTitle><DialogDescription>Consolidated across all active vehicles for the next billing cycle.</DialogDescription></DialogHeader>
        <label className="flex items-center gap-2 text-sm"><Checkbox data-testid="biz-include-deposit" checked={dep} onCheckedChange={(v) => setDep(!!v)} /> Include refundable deposit</label>
        <DialogFooter><Button data-testid="biz-generate-submit" onClick={submit} disabled={busy} className="gap-2">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Generate</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceRow({ inv, upi, onChange }) {
  const confirm = async () => {
    try { await api.post(`/operator/business/invoices/${inv.id}/confirm-payment`, { reference: "" }); toast.success("Invoice marked paid"); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const upiLink = upi?.upi_vpa ? `upi://pay?pa=${upi.upi_vpa}&pn=${encodeURIComponent(upi.payee_name || "")}&am=${inv.total}&cu=INR&tn=${inv.number}` : null;
  return (
    <div data-testid={`biz-invoice-${inv.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-tenant-border px-3 py-2 text-sm">
      <div>
        <p className="font-medium">{inv.number} · ₹{inv.total?.toLocaleString()}</p>
        <p className="text-xs text-muted-foreground">{inv.payment_method === "credit" ? `Credit · due ${new Date(inv.due_date).toLocaleDateString()}` : "UPI QR"}{inv.deposit_amount ? ` · incl. deposit ₹${inv.deposit_amount.toLocaleString()}` : ""}</p>
      </div>
      <div className="flex items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${INVOICE_STATUS[inv.status]}`}>{inv.status}</span>
        {inv.status === "pending" && inv.payment_method === "qr" && upiLink && (
          <Dialog>
            <DialogTrigger asChild><Button data-testid={`biz-invoice-qr-${inv.id}`} size="sm" variant="ghost" className="gap-1"><QrCode className="h-4 w-4" /> QR</Button></DialogTrigger>
            <DialogContent className="sm:max-w-xs text-center">
              <DialogHeader><DialogTitle>Pay ₹{inv.total?.toLocaleString()}</DialogTitle><DialogDescription>{upi.payee_name} · {upi.upi_vpa}</DialogDescription></DialogHeader>
              <div className="mx-auto w-fit rounded-tenant-card border border-tenant-border bg-white p-3"><QRCodeSVG value={upiLink} size={190} /></div>
            </DialogContent>
          </Dialog>
        )}
        {inv.status === "pending" && (
          <Button data-testid={`biz-confirm-invoice-${inv.id}`} size="sm" variant="outline" className="gap-1 text-status-available" onClick={confirm}><CheckCircle2 className="h-3.5 w-3.5" /> Confirm paid</Button>
        )}
      </div>
    </div>
  );
}

function ContractDetail({ contractId, vehicles, upi, onClose, onChanged }) {
  const [c, setC] = useState(null);
  const load = useCallback(async () => {
    const { data } = await api.get(`/operator/business/contracts/${contractId}`); setC(data);
  }, [contractId]);
  useEffect(() => { load(); }, [load]);
  const refresh = () => { load(); onChanged?.(); };

  const act = async (path, msg) => { try { await api.post(`/operator/business/contracts/${contractId}/${path}`); toast.success(msg); refresh(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const returnVehicle = async (vid) => { try { await api.delete(`/operator/business/contracts/${contractId}/vehicles/${vid}`); toast.success("Vehicle returned"); refresh(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-2xl">
        {!c ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div> : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">{c.ref} · {c.company_name}
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS[c.status]}`}>{c.status}</span>
              </DialogTitle>
              <DialogDescription>{c.billing_cycle} · ₹{c.per_vehicle_rate?.toLocaleString()}/vehicle · deposit ₹{c.deposit_per_vehicle?.toLocaleString()} · {c.vehicle_policy} · {c.payment_method === "credit" ? `credit NET-${c.credit_net_days}` : "UPI QR"}</DialogDescription>
            </DialogHeader>

            <div className="flex flex-wrap gap-2">
              {c.status === "draft" && <Button data-testid="biz-activate-btn" size="sm" className="gap-1.5 bg-tenant-primary text-tenant-primary-foreground hover:opacity-90" onClick={() => act("activate", "Contract activated")}><Power className="h-3.5 w-3.5" /> Activate</Button>}
              {c.status !== "ended" && <AssignVehicleDialog contractId={contractId} vehicles={vehicles} onChange={refresh} />}
              {c.status === "active" && <GenerateInvoiceDialog contractId={contractId} onChange={refresh} />}
              {c.status === "active" && <Button data-testid="biz-end-btn" size="sm" variant="outline" className="gap-1.5 text-status-recovery" onClick={() => act("end", "Contract ended")}>End contract</Button>}
            </div>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-muted-foreground">Vehicles ({(c.vehicles || []).filter((v) => v.active).length} active)</h3>
              {(c.vehicles || []).filter((v) => v.active).length === 0 ? <p className="text-xs text-muted-foreground">No active vehicles. Add one to start billing.</p> :
                (c.vehicles || []).filter((v) => v.active).map((v) => (
                  <div key={v.vehicle_id} className="flex items-center justify-between rounded-md border border-tenant-border px-3 py-2 text-sm">
                    <span>{v.reg} · {v.model} <span className="text-xs text-muted-foreground">odo {v.odometer_out ?? "—"} km</span></span>
                    {c.status !== "ended" && c.vehicle_policy === "flexible" || c.status === "draft" ? (
                      <Button data-testid={`biz-return-${v.vehicle_id}`} size="sm" variant="ghost" className="gap-1 text-muted-foreground" onClick={() => returnVehicle(v.vehicle_id)}><Trash2 className="h-3.5 w-3.5" /> Return</Button>
                    ) : null}
                  </div>
                ))}
            </section>

            <section className="space-y-2">
              <h3 className="text-sm font-semibold text-muted-foreground">Invoices</h3>
              {(c.invoices || []).length === 0 ? <p className="text-xs text-muted-foreground">No invoices yet.</p> :
                (c.invoices || []).map((inv) => <InvoiceRow key={inv.id} inv={inv} upi={upi} onChange={refresh} />)}
            </section>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function BusinessFleet() {
  const { ctx } = useOperator();
  const [accounts, setAccounts] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [upi, setUpi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    const [a, c] = await Promise.all([api.get("/operator/business/accounts"), api.get("/operator/business/contracts")]);
    setAccounts(a.data); setContracts(c.data); setLoading(false);
  }, []);
  useEffect(() => { load(); api.get("/operator/payment-config").then(({ data }) => setUpi(data)).catch(() => {}); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Business fleet</h1>
          <p className="mt-1 text-sm text-muted-foreground">B2B contracts — multiple bikes under one company, consolidated recurring billing.</p>
        </div>
        <div className="flex gap-2"><NewAccountDialog onCreated={load} /><NewContractDialog accounts={accounts} onCreated={load} /></div>
      </div>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-muted-foreground">Accounts</h2>
        {accounts.length === 0 ? <p className="text-xs text-muted-foreground">No business accounts yet.</p> : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {accounts.map((a) => (
              <div key={a.id} data-testid={`biz-account-${a.id}`} className="rounded-tenant-card border border-tenant-border bg-tenant-surface p-4 shadow-tenant-soft">
                <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-tenant-primary" /><p className="font-semibold">{a.company_name}</p></div>
                <p className="mt-1 text-xs text-muted-foreground">{a.contact_person} · {a.contact_phone}</p>
                <p className="text-xs text-muted-foreground">{a.gst_number} · {a.contract_count} contract{a.contract_count !== 1 ? "s" : ""}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-muted-foreground">Contracts</h2>
        {contracts.length === 0 ? <p className="text-xs text-muted-foreground">No contracts yet.</p> : (
          <div className="space-y-2">
            {contracts.map((c) => (
              <button key={c.id} data-testid={`biz-contract-row-${c.id}`} onClick={() => setSelected(c.id)}
                className="flex w-full flex-wrap items-center justify-between gap-2 rounded-tenant-card border border-tenant-border bg-tenant-surface p-4 text-left shadow-tenant-soft transition hover:border-tenant-primary/40">
                <div>
                  <p className="font-semibold">{c.ref} · {c.company_name}</p>
                  <p className="text-xs text-muted-foreground">{c.billing_cycle} · ₹{c.per_vehicle_rate?.toLocaleString()}/vehicle · {c.active_vehicle_count} active · {c.vehicle_policy}</p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS[c.status]}`}>{c.status}</span>
              </button>
            ))}
          </div>
        )}
      </section>

      {selected && <ContractDetail contractId={selected} vehicles={ctx?.vehicles || []} upi={upi} onClose={() => setSelected(null)} onChanged={load} />}
    </div>
  );
}
